import { useNavigate } from '@tanstack/react-router';
import { RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import { Checkbox } from '@apps/web/components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { Spinner } from '@apps/web/components/ui/spinner';
import { isInlinePanelVisible } from '@apps/web/features/admin_agent_notes/model/constants';
import { useAgentNotesPage } from '@apps/web/features/admin_agent_notes/model/use_agent_notes_page';
import { NotesFeed } from '@apps/web/features/admin_agent_notes/ui/notes_feed';
import { NotesFilterBar } from '@apps/web/features/admin_agent_notes/ui/notes_filter_bar';

/** Manual refresh button with last-refreshed time; re-renders on a tick. */
function RefreshButton({
    lastRefreshedAt,
    isRefreshing,
    onRefresh,
}: {
    readonly lastRefreshedAt: number | null;
    readonly isRefreshing: boolean;
    readonly onRefresh: () => void;
}) {
    const [, setTick] = useState(0);
    useEffect(() => {
        const interval = setInterval(() => {
            setTick((value) => value + 1);
        }, 5_000);
        return () => {
            clearInterval(interval);
        };
    }, []);

    const agoLabel = (() => {
        if (lastRefreshedAt === null) {
            return null;
        }
        const secondsAgo = Math.max(
            0,
            Math.round((Date.now() - lastRefreshedAt) / 1000),
        );
        return secondsAgo < 60
            ? `${secondsAgo}s ago`
            : `${Math.round(secondsAgo / 60)}m ago`;
    })();

    return (
        <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground h-7 gap-1.5 text-xs whitespace-nowrap"
            disabled={isRefreshing}
            onClick={onRefresh}
            aria-label="Refresh notes"
        >
            {isRefreshing ? (
                <Spinner size="sm" tone="muted" />
            ) : (
                <RefreshCw className="h-3.5 w-3.5" />
            )}
            {agoLabel ? `Refreshed ${agoLabel}` : 'Refresh'}
        </Button>
    );
}

export function NotesTab() {
    const page = useAgentNotesPage();
    const navigate = useNavigate();
    const [discardOpen, setDiscardOpen] = useState(false);
    const [releaseOpen, setReleaseOpen] = useState(false);
    const discardOrganizationLabel =
        page.notes[0]?.organization_name ?? page.search.organization_id ?? '';
    const discardScope =
        page.search.parked === true
            ? 'parked-item view'
            : 'zero-item parked-intent view';

    return (
        <div className="flex flex-col gap-4 lg:min-h-0 lg:flex-1">
            <div className="flex shrink-0 flex-wrap items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                    <NotesFilterBar
                        search={page.search}
                        statusCounts={page.statusCounts}
                        onPatch={page.patchSearch}
                    />
                </div>
                <div className="pt-1">
                    <RefreshButton
                        lastRefreshedAt={page.lastRefreshedAt}
                        isRefreshing={page.isRefreshing}
                        onRefresh={() => {
                            void page.refresh();
                        }}
                    />
                </div>
            </div>
            {page.selectableCount > 0 && page.selectedCount === 0 && (
                <label className="text-muted-foreground flex w-fit shrink-0 cursor-pointer items-center gap-2 text-xs">
                    <Checkbox
                        checked={false}
                        onCheckedChange={page.toggleSelectAllVisible}
                        aria-label="Select all on this page"
                    />
                    Select all on this page
                </label>
            )}
            {page.selectedCount > 0 && (
                <div className="bg-muted/50 flex shrink-0 items-center justify-between gap-3 rounded-lg border px-4 py-2">
                    <span className="flex items-center gap-2 text-sm font-medium">
                        <Checkbox
                            checked={page.allVisibleSelected}
                            onCheckedChange={page.toggleSelectAllVisible}
                            aria-label="Select all on this page"
                        />
                        {page.selectedCount} selected
                    </span>
                    <span className="flex items-center gap-2">
                        <Button
                            variant="ghost"
                            size="sm"
                            disabled={
                                page.isReclassifying ||
                                page.isDiscarding ||
                                page.isReleasing
                            }
                            onClick={page.clearSelection}
                        >
                            Clear
                        </Button>
                        {page.isReleaseScope && (
                            <Button
                                size="sm"
                                className="gap-1.5"
                                disabled={page.isReleasing || page.isDiscarding}
                                onClick={() => {
                                    page.clearReleaseError();
                                    setReleaseOpen(true);
                                }}
                            >
                                {page.isReleasing && (
                                    <Spinner size="sm" tone="muted" />
                                )}
                                Release selected
                            </Button>
                        )}
                        {page.isDiscardScope ? (
                            <Button
                                size="sm"
                                variant="destructive"
                                className="gap-1.5"
                                disabled={page.isDiscarding || page.isReleasing}
                                onClick={() => {
                                    page.clearDiscardError();
                                    setDiscardOpen(true);
                                }}
                            >
                                {page.isDiscarding && (
                                    <Spinner size="sm" tone="muted" />
                                )}
                                Discard selected
                            </Button>
                        ) : (
                            <Button
                                size="sm"
                                className="gap-1.5"
                                disabled={page.isReclassifying}
                                onClick={() => {
                                    void page.reclassifySelected();
                                }}
                            >
                                {page.isReclassifying && (
                                    <Spinner size="sm" tone="muted" />
                                )}
                                Reclassify
                            </Button>
                        )}
                    </span>
                </div>
            )}
            {page.hasError ? (
                <p className="text-destructive rounded-md border px-3 py-6 text-center text-sm">
                    Notes failed to load. Refresh to try again.
                </p>
            ) : (
                <NotesFeed
                    notes={page.notes}
                    isLoading={page.isLoading}
                    selectedNote={page.selectedNote}
                    detailError={page.selectedNoteDetailError}
                    isRetrying={page.isRetrying}
                    hasPrevious={page.hasPreviousNote}
                    hasNext={page.hasNextNote}
                    page={page.search.page}
                    totalPages={page.totalPages}
                    total={page.total}
                    selectedNoteIds={page.selectedNoteIds}
                    isNoteSelectable={page.isNoteSelectable}
                    onToggleSelect={page.toggleNoteSelected}
                    onSelectNote={(noteId) => {
                        if (isInlinePanelVisible()) {
                            page.patchSearch({ note: noteId });
                        } else {
                            void navigate({
                                to: '/admin/agent-notes/$note-id',
                                params: { 'note-id': noteId },
                                search: { ...page.search, note: undefined },
                            });
                        }
                    }}
                    onPageChange={(nextPage) => {
                        page.patchSearch({ page: nextPage });
                    }}
                    onRetry={(noteId, content) =>
                        page.retryClassification(noteId, content)
                    }
                    onNavigate={page.navigateNote}
                />
            )}
            <Dialog
                open={releaseOpen}
                onOpenChange={(open) => {
                    setReleaseOpen(open);
                    if (!open) {
                        page.clearReleaseError();
                    }
                }}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Release selected parked items</DialogTitle>
                        <DialogDescription>
                            Release the parked items on {page.selectedCount}{' '}
                            selected{' '}
                            {page.selectedCount === 1 ? 'note' : 'notes'} for{' '}
                            {discardOrganizationLabel}. Phoebe starts learning
                            from them right away, and a release cannot be
                            undone. An item stays parked while earlier notes for
                            the same caregiver are still unfinished.
                        </DialogDescription>
                    </DialogHeader>
                    {page.releaseError !== null && (
                        <p className="text-destructive text-sm">
                            {page.releaseError}
                        </p>
                    )}
                    <DialogFooter>
                        <Button
                            variant="outline"
                            disabled={page.isReleasing}
                            onClick={() => {
                                setReleaseOpen(false);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            disabled={page.isReleasing}
                            onClick={() => {
                                void page.releaseSelected().then((released) => {
                                    if (released) {
                                        setReleaseOpen(false);
                                    }
                                });
                            }}
                        >
                            {page.isReleasing && (
                                <Spinner size="sm" tone="muted" />
                            )}
                            Confirm release
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <Dialog
                open={discardOpen}
                onOpenChange={(open) => {
                    setDiscardOpen(open);
                    if (!open) {
                        page.clearDiscardError();
                    }
                }}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Discard selected parked work</DialogTitle>
                        <DialogDescription>
                            Discard {page.selectedCount} selected notes from the{' '}
                            {discardScope} for {discardOrganizationLabel}.
                            Side-effect-free notes are removed as notes; a note
                            with a released sibling removes only its eligible
                            unreleased parked items.
                        </DialogDescription>
                    </DialogHeader>
                    {page.discardError !== null && (
                        <p className="text-destructive text-sm">
                            {page.discardError}
                        </p>
                    )}
                    <DialogFooter>
                        <Button
                            variant="outline"
                            disabled={page.isDiscarding}
                            onClick={() => {
                                setDiscardOpen(false);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            disabled={page.isDiscarding}
                            onClick={() => {
                                void page
                                    .discardSelected()
                                    .then((discarded) => {
                                        if (discarded) {
                                            setDiscardOpen(false);
                                        }
                                    });
                            }}
                        >
                            {page.isDiscarding && (
                                <Spinner size="sm" tone="muted" />
                            )}
                            Confirm discard
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
